<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import AgentPickerModal from '../components/AgentPickerModal.vue'
import SalesOrderPickerModal from '../components/SalesOrderPickerModal.vue'
import { createProductionOrder, getTechnicalPassport, getTechnicalPassportMaterialRequirements, listAgentScopeOptions } from '../productService'
import type { AgentScopeOption, ProductionOrder, ProductionOrderKnittingColor, SalesOrderOption, TechnicalPassport, TechnicalPassportMaterialRequirements } from '../types'

const route = useRoute()
const client = useApiClient()
const { t } = useI18n()
const passportId = computed(() => Number(route.query.technical_passport_id))
const passport = ref<TechnicalPassport | null>(null)
const requirements = ref<TechnicalPassportMaterialRequirements | null>(null)
const createdOrder = ref<ProductionOrder | null>(null)
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const submitError = ref('')
const fieldErrors = reactive<Record<string, string>>({})
const departments = ref<AgentScopeOption[]>([])
const warehouses = ref<AgentScopeOption[]>([])
const departmentPickerOpen = ref(false)
const salesOrderPickerOpen = ref(false)
const sourceWarehousePickerOpen = ref(false)
const destinationWarehousePickerOpen = ref(false)
const form = reactive({
  mo_code: `MO-${new Date().toISOString().slice(0, 10).replaceAll('-', '')}-`,
  department_agent_id: undefined as number | undefined,
  planned_quantity: 1000,
  order_id: undefined as number | undefined,
  source_warehouse_agent_id: undefined as number | undefined,
  destination_warehouse_agent_id: undefined as number | undefined,
  start_date: '',
  notes: '',
  knitting_colors: [] as ProductionOrderKnittingColor[],
})

const selectedMachine = computed(() => passport.value?.machines?.find((machine) => Boolean(machine.is_compatible)))
const materialRows = computed(() => requirements.value?.materials ?? [])
const selectedDepartment = computed(() => departments.value.find((item) => item.id === form.department_agent_id) ?? null)
const selectedSourceWarehouse = computed(() => warehouses.value.find((item) => item.id === form.source_warehouse_agent_id) ?? null)
const selectedDestinationWarehouse = computed(() => warehouses.value.find((item) => item.id === form.destination_warehouse_agent_id) ?? null)
const selectedSalesOrder = ref<SalesOrderOption | null>(null)

function validate(): boolean {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key])
  if (!form.mo_code.trim()) fieldErrors.mo_code = t('engineering.productionOrder.moCodeRequired')
  if (!form.department_agent_id || form.department_agent_id <= 0) fieldErrors.department_agent_id = t('engineering.productionOrder.departmentRequired')
  if (!form.planned_quantity || form.planned_quantity <= 0) fieldErrors.planned_quantity = t('engineering.productionOrder.plannedPairsRequired')
  if (form.source_warehouse_agent_id && form.source_warehouse_agent_id === form.destination_warehouse_agent_id) {
    fieldErrors.destination_warehouse_agent_id = t('engineering.productionOrder.warehousesMustDiffer')
  }
  form.knitting_colors.forEach((row, index) => {
    if (!row.color_name.trim() || !row.yarn_code?.trim()) fieldErrors[`color_${index}`] = t('engineering.productionOrder.colorAndYarnRequired')
    else if (!Number.isInteger(Number(row.desyatka_quantity)) || Number(row.desyatka_quantity) <= 0) fieldErrors[`color_${index}`] = t('engineering.productionOrder.desyatkaRequired')
  })
  return Object.keys(fieldErrors).length === 0
}

async function calculateRequirements(): Promise<void> {
  if (!passport.value || form.planned_quantity <= 0) return
  try {
    requirements.value = await getTechnicalPassportMaterialRequirements(client, passport.value.id, Number(form.planned_quantity))
  } catch (error: unknown) {
    submitError.value = formatApiError(error)
  }
}

async function load(): Promise<void> {
  if (!Number.isInteger(passportId.value) || passportId.value <= 0) {
    errorMessage.value = t('engineering.productionOrder.passportRequired')
    return
  }
  loading.value = true
  try {
    passport.value = await getTechnicalPassport(client, passportId.value)
    const [loadedDepartments, loadedWarehouses] = await Promise.all([
      listAgentScopeOptions(client, 'department').catch(() => []),
      listAgentScopeOptions(client, 'warehouse').catch(() => []),
    ])
    departments.value = loadedDepartments
    warehouses.value = loadedWarehouses
    if (passport.value.status !== 'approved') {
      errorMessage.value = t('engineering.productionOrder.passportMustBeApproved')
      return
    }
    form.knitting_colors = (passport.value.threads ?? []).map((thread) => ({
      color_name: thread.thread_color_name ?? '',
      yarn_code: thread.thread_code ?? '',
      desyatka_quantity: 1,
    }))
    await calculateRequirements()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

function selectDepartment(agent: AgentScopeOption): void {
  form.department_agent_id = agent.id
  departmentPickerOpen.value = false
}

function selectSalesOrder(order: SalesOrderOption): void {
  form.order_id = order.id
  selectedSalesOrder.value = order
  salesOrderPickerOpen.value = false
}

function selectSourceWarehouse(agent: AgentScopeOption): void {
  form.source_warehouse_agent_id = agent.id
  sourceWarehousePickerOpen.value = false
}

function selectDestinationWarehouse(agent: AgentScopeOption): void {
  form.destination_warehouse_agent_id = agent.id
  destinationWarehousePickerOpen.value = false
}

async function submit(): Promise<void> {
  if (!passport.value || !validate()) return
  saving.value = true
  submitError.value = ''
  try {
    createdOrder.value = await createProductionOrder(client, {
      mo_code: form.mo_code.trim(),
      product_id: passport.value.product_id,
      department_agent_id: Number(form.department_agent_id),
      planned_quantity: Number(form.planned_quantity),
      order_id: form.order_id ? Number(form.order_id) : undefined,
      source_warehouse_agent_id: form.source_warehouse_agent_id ? Number(form.source_warehouse_agent_id) : undefined,
      destination_warehouse_agent_id: form.destination_warehouse_agent_id ? Number(form.destination_warehouse_agent_id) : undefined,
      start_date: form.start_date || undefined,
      notes: form.notes.trim() || undefined,
      status: 'planned',
      knitting_passport: {
        equipment_reference_id: selectedMachine.value?.equipment_reference_id,
        equipment_name: selectedMachine.value?.machine_name,
        knitting_type: passport.value.sock_type,
        passport_name: `Technical passport #${passport.value.id}`,
        pack_quantity: passport.value.packaging?.[0]?.quantity,
        machine_details: selectedMachine.value?.needle_count ? `${selectedMachine.value.needle_count} needles` : undefined,
      },
      knitting_colors: form.knitting_colors.map((row) => ({
        color_name: row.color_name.trim(),
        yarn_code: row.yarn_code?.trim(),
        desyatka_quantity: Number(row.desyatka_quantity),
      })),
    })
  } catch (error: unknown) {
    submitError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

watch(() => form.planned_quantity, () => { requirements.value = null })
onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :data-tooltip="t('engineering.productionOrder.backToPassport')" :to="passport ? `/technical-passports/${passport.id}` : '/products'">
      {{ t('engineering.productionOrder.backToPassport') }}
    </RouterLink>
    <div class="page-toolbar__actions">
      <button type="submit" form="guided-order-form" :disabled="loading || saving">{{ saving ? t('engineering.productionOrder.creating') : t('engineering.productionOrder.createPlannedOrder') }}</button>
    </div>
  </div>
  <div v-if="loading" class="page-state">{{ t('engineering.productionOrder.loadingPlan') }}</div>
  <div v-else-if="errorMessage" class="page-state page-state--error" role="alert">{{ errorMessage }}</div>
  <section v-else-if="createdOrder" class="success-card">
    <p class="eyebrow">{{ t('engineering.productionOrder.createdEyebrow') }}</p>
    <h2>{{ createdOrder.mo_code }}</h2>
    <p>{{ t('engineering.productionOrder.plannedQuantityPairs', { count: createdOrder.planned_quantity }) }}</p>
    <p>{{ t('engineering.productionOrder.status') }}: {{ t(`engineering.state.${createdOrder.status}`) }}</p>
    <RouterLink :to="`/technical-passports/${passportId}`">{{ t('engineering.productionOrder.returnToPassport') }}</RouterLink>
  </section>
  <form v-else-if="passport" id="guided-order-form" class="guided-form" @submit.prevent="submit">
    <header>
      <div>
        <p class="eyebrow">{{ t('engineering.productionOrder.guidedEyebrow') }}</p>
        <h2>{{ passport.product_name }}</h2>
        <p>{{ t('engineering.productionOrder.guidedDescription', { id: passport.id }) }}</p>
      </div>
    </header>

    <p v-if="submitError" class="form-error" role="alert">{{ submitError }}</p>
    <section>
      <h3>{{ t('engineering.productionOrder.sectionDefinition') }}</h3>
      <div class="form-grid">
        <label>{{ t('engineering.productionOrder.moCode') }} *<input v-model="form.mo_code" maxlength="60" type="text"><small v-if="fieldErrors.mo_code">{{ fieldErrors.mo_code }}</small></label>
        <label>{{ t('engineering.productionOrder.department') }} *
          <button class="product-select" type="button" @click="departmentPickerOpen = true">
            <span>{{ selectedDepartment?.name || t('engineering.productionOrder.selectDepartment') }}</span>
            <small>{{ form.department_agent_id ? `#${form.department_agent_id}` : t('engineering.productionOrder.browseDepartments') }}</small>
          </button>
          <small v-if="fieldErrors.department_agent_id">{{ fieldErrors.department_agent_id }}</small>
        </label>
        <label>{{ t('engineering.productionOrder.plannedPairs') }} *<input v-model.number="form.planned_quantity" min="1" step="1" type="number"><small v-if="fieldErrors.planned_quantity">{{ fieldErrors.planned_quantity }}</small></label>
        <label>{{ t('engineering.productionOrder.departmentId') }}<input :value="form.department_agent_id ?? ''" readonly type="text"></label>
        <label>{{ t('engineering.productionOrder.salesOrder') }}
          <button class="product-select" type="button" @click="salesOrderPickerOpen = true">
            <span>{{ selectedSalesOrder?.so_code || t('engineering.productionOrder.selectSalesOrder') }}</span>
            <small>{{ selectedSalesOrder?.customer_name || (form.order_id ? `#${form.order_id}` : t('engineering.productionOrder.browseSalesOrders')) }}</small>
          </button>
        </label>
        <label>{{ t('engineering.productionOrder.salesOrderId') }}<input :value="form.order_id ?? ''" readonly type="text"></label>
        <label>{{ t('engineering.productionOrder.sourceWarehouse') }}
          <button class="product-select" type="button" @click="sourceWarehousePickerOpen = true">
            <span>{{ selectedSourceWarehouse?.name || t('engineering.productionOrder.selectSourceWarehouse') }}</span>
            <small>{{ form.source_warehouse_agent_id ? `#${form.source_warehouse_agent_id}` : t('engineering.productionOrder.browseWarehouses') }}</small>
          </button>
        </label>
        <label>{{ t('engineering.productionOrder.sourceWarehouseId') }}<input :value="form.source_warehouse_agent_id ?? ''" readonly type="text"></label>
        <label>{{ t('engineering.productionOrder.destinationWarehouse') }}
          <button class="product-select" type="button" @click="destinationWarehousePickerOpen = true">
            <span>{{ selectedDestinationWarehouse?.name || t('engineering.productionOrder.selectDestinationWarehouse') }}</span>
            <small>{{ form.destination_warehouse_agent_id ? `#${form.destination_warehouse_agent_id}` : t('engineering.productionOrder.browseWarehouses') }}</small>
          </button>
          <small v-if="fieldErrors.destination_warehouse_agent_id">{{ fieldErrors.destination_warehouse_agent_id }}</small>
        </label>
        <label>{{ t('engineering.productionOrder.destinationWarehouseId') }}<input :value="form.destination_warehouse_agent_id ?? ''" readonly type="text"></label>
        <label>{{ t('engineering.productionOrder.plannedStart') }}<input v-model="form.start_date" type="datetime-local"></label>
        <label class="wide">{{ t('engineering.productionOrder.notes') }}<textarea v-model="form.notes" rows="3"></textarea></label>
      </div>
    </section>

    <section>
      <h3>{{ t('engineering.productionOrder.sectionPassportSetup') }}</h3>
      <div class="summary-grid">
        <div><span>{{ t('engineering.productionOrder.machine') }}</span><strong>{{ selectedMachine?.machine_name || t('engineering.productionOrder.notNamed') }}</strong><small>{{ selectedMachine?.equipment_reference_id ? t('engineering.productionOrder.referenceNumber', { id: selectedMachine.equipment_reference_id }) : '—' }}</small></div>
        <div><span>{{ t('engineering.productionOrder.needles') }}</span><strong>{{ selectedMachine?.needle_count || '—' }}</strong></div>
        <div><span>{{ t('engineering.productionOrder.sockType') }}</span><strong>{{ passport.sock_type }}</strong></div>
        <div><span>{{ t('engineering.productionOrder.packagingPerPair') }}</span><strong>{{ passport.packaging?.[0]?.quantity ?? '—' }}</strong></div>
      </div>
    </section>

    <section>
      <h3>{{ t('engineering.productionOrder.sectionColorPlan') }}</h3>
      <p v-if="!form.knitting_colors.length" class="empty-row">{{ t('engineering.productionOrder.noThreadRows') }}</p>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.knitting_colors" :key="index" class="color-row">
          <label>{{ t('engineering.productionOrder.color') }}<input v-model="row.color_name" readonly type="text"></label>
          <label>{{ t('engineering.productionOrder.yarnCode') }}<input v-model="row.yarn_code" readonly type="text"></label>
          <label>{{ t('engineering.productionOrder.desyatkaQuantity') }} *<input v-model.number="row.desyatka_quantity" min="1" step="1" type="number"></label>
          <small v-if="fieldErrors[`color_${index}`]" class="row-error">{{ fieldErrors[`color_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section>
      <div class="section-heading">
        <div><h3>{{ t('engineering.productionOrder.sectionMaterials') }}</h3><p>{{ t('engineering.productionOrder.materialsDescription', { count: form.planned_quantity }) }}</p></div>
        <button class="secondary-button" type="button" @click="calculateRequirements">{{ t('engineering.productionOrder.refreshPreview') }}</button>
      </div>
      <div v-if="!requirements" class="empty-row">{{ t('engineering.productionOrder.refreshPreviewHint') }}</div>
      <div v-else-if="!materialRows.length" class="empty-row">{{ t('engineering.productionOrder.noMaterials') }}</div>
      <table v-else>
        <thead><tr><th>{{ t('engineering.productionOrder.stage') }}</th><th>{{ t('engineering.productionOrder.material') }}</th><th>{{ t('engineering.productionOrder.perPair') }}</th><th>{{ t('engineering.productionOrder.waste') }}</th><th>{{ t('engineering.productionOrder.required') }}</th></tr></thead>
        <tbody><tr v-for="row in materialRows" :key="`${row.stage_code}-${row.material_product_id}`">
          <td>{{ row.stage_code }}</td><td>{{ row.material_name || t('engineering.productionOrder.productNumber', { id: row.material_product_id }) }}</td>
          <td>{{ row.consumption_per_pair }} {{ row.uom }}</td><td>{{ row.waste_percent }}%</td>
          <td><strong>{{ row.required_quantity }} {{ row.uom }}</strong></td>
        </tr></tbody>
      </table>
    </section>
  </form>
  <AgentPickerModal :open="departmentPickerOpen" :title="t('engineering.productionOrder.selectDepartment')" :eyebrow="t('engineering.productionOrder.browseDepartmentsTitle')" :search-label="t('engineering.productionOrder.searchDepartments')" :search-placeholder="t('engineering.productionOrder.searchDepartmentPlaceholder')" :empty-message="t('engineering.productionOrder.noDepartments')" :items="departments" @close="departmentPickerOpen = false" @select="selectDepartment" />
  <SalesOrderPickerModal :open="salesOrderPickerOpen" :title="t('engineering.productionOrder.selectSalesOrder')" @close="salesOrderPickerOpen = false" @select="selectSalesOrder" />
  <AgentPickerModal :open="sourceWarehousePickerOpen" :title="t('engineering.productionOrder.selectSourceWarehouse')" :eyebrow="t('engineering.productionOrder.browseWarehousesTitle')" :search-label="t('engineering.productionOrder.searchWarehouses')" :search-placeholder="t('engineering.productionOrder.searchWarehousePlaceholder')" :empty-message="t('engineering.productionOrder.noWarehouses')" :items="warehouses" @close="sourceWarehousePickerOpen = false" @select="selectSourceWarehouse" />
  <AgentPickerModal :open="destinationWarehousePickerOpen" :title="t('engineering.productionOrder.selectDestinationWarehouse')" :eyebrow="t('engineering.productionOrder.browseWarehousesTitle')" :search-label="t('engineering.productionOrder.searchWarehouses')" :search-placeholder="t('engineering.productionOrder.searchWarehousePlaceholder')" :empty-message="t('engineering.productionOrder.noWarehouses')" :items="warehouses" @close="destinationWarehousePickerOpen = false" @select="selectDestinationWarehouse" />
</template>

<style scoped>
.back-link { display: inline-block; margin-bottom: 20px; color: #285b9a; font-weight: 700; }
.page-state { display: grid; min-height: 240px; place-items: center; }
.page-state--error, .form-error, label small, .row-error { color: #852d22; }
.guided-form { display: grid; gap: 16px; }
header, .section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.eyebrow { margin: 0 0 5px; color: #66726e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
h2, h3 { margin-top: 0; }
header p:last-child, .section-heading p { color: #66726e; }
section { padding: 22px; border: 1px solid #d7dfdc; background: #fff; }
.form-grid, .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
.wide { grid-column: 1 / -1; }
input, textarea { width: 100%; box-sizing: border-box; padding: 9px 10px; border: 1px solid #b9c5c1; font: inherit; }
.product-select { display: flex; align-items: center; justify-content: space-between; min-height: 40px; padding: 8px 10px; border: 1px solid #aebdcc; color: #173454; background: #fff; font: inherit; text-align: left; }
.product-select small { color: #66726e; font-weight: 500; }
button { padding: 10px 16px; border: 0; color: #fff; background: #07996f; font-weight: 700; }
button:disabled { opacity: .6; }
.secondary-button { border: 1px solid #b9c5c1; color: #285b9a; background: #fff; }
.summary-grid div { display: grid; gap: 4px; padding: 14px; background: #f3f6f5; }
.summary-grid span, .summary-grid small { color: #66726e; }
.repeatable-list { display: grid; gap: 10px; }
.color-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; padding: 16px; border: 1px solid #d7dfdc; background: #f8faf9; }
.row-error { grid-column: 1 / -1; }
.empty-row { padding: 14px; color: #66726e; background: #f3f6f5; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 11px; border-bottom: 1px solid #d7dfdc; text-align: left; }
th { color: #53625d; font-size: 12px; text-transform: uppercase; }
.success-card { max-width: 620px; }
@media (max-width: 700px) { header, .section-heading { flex-direction: column; } .form-grid, .summary-grid, .color-row { grid-template-columns: 1fr; } .wide { grid-column: auto; } section { overflow-x: auto; } }
</style>
